/**
 * Berapa lama tiap tahap pengiriman, dan bukti bahwa kurir benar-benar sampai — MURNI.
 *
 * ADA KARENA Owner ingin melihat jejak kurir di peta seperti Strava, dan itu mustahil di PWA:
 * pembacaan lokasi web berhenti begitu layar terkunci. Tetapi dua dari tiga hal yang ingin ia
 * ketahui TIDAK membutuhkan rute sama sekali — "berapa lama di jalan, dan benarkah sampai" serta
 * "kenapa pengiriman ini lama" dijawab oleh selisih waktu antar transisi, yang datanya sudah
 * lengkap sejak awal. Ini menjawab keduanya tanpa merekam apa pun yang baru.
 *
 * WAKTU SERVER, BUKAN WAKTU PERANGKAT. Jam ponsel dapat meleset berjam-jam, dan durasi yang
 * dihitung darinya bisa negatif atau menggelembung tanpa ada yang salah di lapangan. Setiap
 * event membawa keduanya; yang dipakai di sini selalu `occurredAtServer`.
 */

export interface EventRingkas {
  readonly eventType: string;
  readonly occurredAtServer: string;
  readonly sequenceNo: number;
  readonly distanceToDestinationMeters?: number | null;
}

export interface Tahap {
  readonly label: string;
  readonly menit: number;
}

/** Urutan hidup sebuah pengiriman, dan nama tahap DI ANTARA dua transisi. */
const TAHAPAN: readonly { readonly dari: string; readonly ke: string; readonly label: string }[] = [
  { dari: 'assigned', ke: 'accepted', label: 'Menunggu diterima kurir' },
  { dari: 'accepted', ke: 'started', label: 'Menunggu berangkat' },
  { dari: 'started', ke: 'arrived', label: 'Di jalan' },
  { dari: 'arrived', ke: 'completed', label: 'Di lokasi pelanggan' },
];

function waktuPertama(events: readonly EventRingkas[], tipe: string): number | null {
  const cocok = events
    .filter((e) => e.eventType === tipe)
    .sort((a, b) => a.sequenceNo - b.sequenceNo)[0];
  if (!cocok) return null;
  const t = new Date(cocok.occurredAtServer).getTime();
  return Number.isFinite(t) ? t : null;
}

/**
 * Lama tiap tahap yang BENAR-BENAR terjadi.
 *
 * Tahap yang salah satu ujungnya belum ada TIDAK ditampilkan — bukan ditampilkan sebagai nol.
 * Nol terbaca sebagai "berlangsung seketika", padahal artinya "belum terjadi", dan keduanya
 * menuntun ke kesimpulan yang berlawanan saat menilai kenapa sebuah pengiriman lama.
 */
export function tahapanTugas(events: readonly EventRingkas[]): readonly Tahap[] {
  const hasil: Tahap[] = [];
  for (const t of TAHAPAN) {
    const a = waktuPertama(events, t.dari);
    const b = waktuPertama(events, t.ke);
    if (a === null || b === null) continue;
    const menit = Math.round((b - a) / 60_000);
    // Selisih negatif berarti jam server mundur — tidak mungkin dalam praktik, tetapi
    // menampilkannya sebagai durasi akan lebih membingungkan daripada menghilangkannya.
    if (menit < 0) continue;
    hasil.push({ label: t.label, menit });
  }
  return hasil;
}

/** Total dari ditugaskan sampai selesai; `null` bila belum selesai. */
export function totalMenit(events: readonly EventRingkas[]): number | null {
  const mulai = waktuPertama(events, 'assigned') ?? waktuPertama(events, 'accepted');
  const selesai = waktuPertama(events, 'completed') ?? waktuPertama(events, 'failed');
  if (mulai === null || selesai === null) return null;
  const menit = Math.round((selesai - mulai) / 60_000);
  return menit < 0 ? null : menit;
}

export interface BuktiSampai {
  readonly jarakMeter: number;
  /** Benar bila titik tiba berada dalam ambang yang wajar dari tujuan. */
  readonly dekat: boolean;
}

/**
 * Seberapa dekat kurir dari tujuan saat menekan "tiba" — jawaban atas "benarkah dia sampai".
 *
 * Ambang 200 m dipilih untuk ALAMAT, bukan untuk titik: koordinat tujuan sering berasal dari
 * geocoding alamat, yang di Indonesia biasa meleset puluhan hingga ratusan meter. Ambang yang
 * ketat akan menuduh kurir yang sebenarnya berdiri di depan pintu.
 *
 * Ia MEMANDU, bukan menghakimi: yang ditampilkan tetap angka jaraknya, supaya pembaca menilai
 * sendiri, bukan hanya lencana "benar/salah".
 */
export const AMBANG_DEKAT_METER = 200;

export function buktiSampai(events: readonly EventRingkas[]): BuktiSampai | null {
  const tiba = events
    .filter((e) => e.eventType === 'arrived')
    .sort((a, b) => a.sequenceNo - b.sequenceNo)[0];
  const jarak = tiba?.distanceToDestinationMeters;
  if (jarak === null || jarak === undefined || !Number.isFinite(jarak)) return null;
  return { jarakMeter: Math.round(jarak), dekat: jarak <= AMBANG_DEKAT_METER };
}

/** Durasi sebagai kalimat pendek. */
export function lamaSingkat(menit: number): string {
  if (menit < 1) return 'kurang dari 1 menit';
  if (menit < 60) return `${menit} menit`;
  const jam = Math.floor(menit / 60);
  const sisa = menit % 60;
  return sisa === 0 ? `${jam} jam` : `${jam} jam ${sisa} menit`;
}
